/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

export type NamingConventionInput = string | string[];

function splitStringIntoWords(value: string): string[] {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return [];
    }

    const separatedValue = trimmedValue
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

    return separatedValue
        .split(/[\s._-]+/)
        .map((word) => word.trim())
        .filter(Boolean)
        .map((word) => word.toLowerCase());
}

function upperCaseFirstCharacter(value: string): string {
    if (!value) {
        return value;
    }

    return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

export class NamingConventions {
    private readonly input: NamingConventionInput;

    private readonly words: string[];

    constructor(input: NamingConventionInput) {
        this.input = Array.isArray(input) ? [...input] : input;
        this.words = NamingConventions.normalizeWords(input);
    }

    static normalizeWords(input: NamingConventionInput): string[] {
        if (Array.isArray(input)) {
            return input.flatMap((value) => splitStringIntoWords(value));
        }

        return splitStringIntoWords(input);
    }

    getRaw(): NamingConventionInput {
        return Array.isArray(this.input) ? [...this.input] : this.input;
    }

    getWords(): string[] {
        return [...this.words];
    }

    getTitle(): string {
        return this.words
            .map((word) => upperCaseFirstCharacter(word))
            .join(' ');
    }

    getPascalCase(): string {
        return this.words
            .map((word) => upperCaseFirstCharacter(word))
            .join('');
    }

    getCamelCase(): string {
        const [firstWord = '', ...otherWords] = this.words;

        return [
            firstWord.toLowerCase(),
            ...otherWords.map((word) => upperCaseFirstCharacter(word)),
        ].join('');
    }

    getUnderscored(): string {
        return this.words.join('_');
    }

    getConstant(): string {
        return this.words.join('_').toUpperCase();
    }

    getConfig(): string {
        return this.words.join('.');
    }

    getSeparated(): string {
        return this.words.join('-');
    }
}
